import { PAN_MARGIN_RATIO } from '@/config';
type TClampViewportOffsetParams = {
    // Unzoomed content extent — the print surface, not the canvas element.
    contentHeight: number;
    contentWidth: number;
    offsetX: number;
    offsetY: number;
    viewportHeight: number;
    viewportWidth: number;
    zoom: number;
};

// Keeps a viewport translation within panning range, on one axis.
//
// The base interval is the two ends of `[0, viewport - content]`, whichever way
// round they fall. Zoomed in the content is larger, so that interval keeps the
// viewport inside the content; zoomed out it keeps the content inside the
// viewport. Either way the design cannot be pushed out of sight.
//
// That alone is too tight to work on a border: the edge of the design stops at
// the edge of the screen. PAN_MARGIN_RATIO widens both ends by a share of the
// viewport, so an edge can be dragged inward — at 0.5, all the way to the
// centre.
function _clampAxis(offset: number, viewportSize: number, contentSize: number): number {
    const slack = viewportSize - contentSize;
    const margin = viewportSize * PAN_MARGIN_RATIO;
    return Math.min(Math.max(offset, Math.min(slack, 0) - margin), Math.max(slack, 0) + margin);
}

// Applied after zooming as well as after panning: zoomToPoint does not clamp,
// so a zoom could otherwise leave the viewport out of range and the next pan
// would snap it back in one frame — read on screen as a jump.
function clampViewportOffset(params: TClampViewportOffsetParams): { x: number; y: number } {
    return {
        x: _clampAxis(params.offsetX, params.viewportWidth, params.contentWidth * params.zoom),
        y: _clampAxis(params.offsetY, params.viewportHeight, params.contentHeight * params.zoom),
    };
}

export { clampViewportOffset };
